import { motion } from "framer-motion";
import { CarFront, Copy, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import PlaceholderImage from "./PlaceholderImage";
import MagneticButton from "./MagneticButton";
import { trackEvent } from "@/lib/analytics";
import { PARTNERS } from "@/config/content";

// Getrackter Redirect über das eigene Backend (protokolliert den Klick,
// leitet dann mit UTM-Parametern zu Rent500 weiter).
export const RENT500_TRACK_URL = `${process.env.REACT_APP_BACKEND_URL}/api/go/rent500`;

const EASE = [0.16, 1, 0.3, 1];

const reveal = {
  hidden: { opacity: 0, y: 32 },
  show: { opacity: 1, y: 0, transition: { duration: 0.75, ease: EASE } },
};

const HEE_SERVICES = [
  "Professionelle Videos & Werbevideos",
  "Fotos & Social-Media-Content",
  "Videoproduktionen für Unternehmen",
  "Produktfotos für deine Projekte",
  "Weitere kreative Medienproduktionen",
];

function VehicleCard({ vehicle, index }) {
  return (
    <motion.div
      variants={reveal}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "80px" }}
      transition={{ delay: index * 0.1 }}
      data-testid={`rent500-vehicle-${index}`}
      className="border border-black/10 bg-white transition-[transform,box-shadow,border-color] duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_48px_-20px_rgba(0,0,0,0.2)] hover:border-gold/40"
    >
      {vehicle.image ? (
        <div className="img-frame overflow-hidden">
          <img src={vehicle.image} alt={vehicle.name} loading="lazy" className="w-full aspect-[16/10] object-cover" />
        </div>
      ) : (
        <PlaceholderImage
          icon={CarFront}
          label="Originalbild folgt"
          sublabel="Foto von Rent500"
          className="aspect-[16/10]"
          testId={`rent500-vehicle-image-${index}`}
        />
      )}
      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-display uppercase tracking-tight text-lg">{vehicle.name}</p>
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mt-1">{vehicle.detail}</p>
          </div>
          <span className="shrink-0 border border-black/10 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-zinc-600">
            {vehicle.specs}
          </span>
        </div>
        <p className="mt-4 font-editorial italic text-xl text-gold">{vehicle.price}</p>
      </div>
    </motion.div>
  );
}

export default function Partners() {
  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(PARTNERS.rent500.discountCode);
      toast.success(`Code ${PARTNERS.rent500.discountCode} kopiert.`);
    } catch {
      toast.info(`Code: ${PARTNERS.rent500.discountCode}`);
    }
  };

  return (
    <section id="partner" data-testid="partners-section" className="px-6 lg:px-12 py-24 lg:py-40">
      <div className="max-w-[1600px] mx-auto">
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "100px" }}
          className="mb-16 lg:mb-24 max-w-3xl"
        >
          <p className="text-xs uppercase tracking-[0.35em] text-zinc-500 mb-8">Meine Partner</p>
          <h2
            data-testid="partners-headline"
            className="font-display uppercase tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-6xl"
          >
            Vorteile <span className="text-zinc-400">über mich.</span>
          </h2>
        </motion.div>

        {/* RENT500 */}
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "100px" }}
          data-testid="partner-rent500"
          className="border-t border-black/5 pt-16 lg:pt-24 mb-24 lg:mb-32"
        >
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start mb-14">
            <div className="lg:col-span-7">
              <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">
                {PARTNERS.rent500.location}
              </p>
              <h3 className="font-display uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl mb-6">
                Rent500
              </h3>
              <p className="text-base lg:text-lg leading-relaxed text-zinc-500 max-w-xl mb-8">
                Du möchtest einen besonderen Wagen fahren? Mit meinem
                persönlichen Code erhältst du bei Rent500{" "}
                <strong className="text-ink font-medium">10 % Rabatt</strong>.
              </p>
              <MagneticButton>
                <a
                  href={RENT500_TRACK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="rent500-link-button"
                  className="group relative overflow-hidden inline-flex bg-ink text-paper px-8 py-4 text-xs uppercase tracking-[0.25em]"
                >
                  <span className="absolute inset-0 bg-gold translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
                  <span className="relative flex items-center gap-3 group-hover:text-ink transition-colors duration-500">
                    Zu Rent500 <ArrowUpRight size={16} />
                  </span>
                </a>
              </MagneticButton>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-ink text-paper p-8 lg:p-10" data-testid="rent500-discount-box">
                <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-6">
                  Dein persönlicher Rabattcode
                </p>
                <button
                  onClick={copyCode}
                  data-testid="rent500-code-copy"
                  className="group flex items-center gap-4 mb-6"
                  title="Code kopieren"
                >
                  <span className="font-display font-bold uppercase tracking-tight text-5xl lg:text-6xl group-hover:text-goldlight transition-colors duration-300">
                    {PARTNERS.rent500.discountCode}
                  </span>
                  <Copy size={20} className="text-zinc-500 group-hover:text-goldlight transition-colors duration-300" />
                </button>
                <p className="font-editorial italic text-3xl text-goldlight">
                  {PARTNERS.rent500.discountPercent} % Rabatt
                </p>
              </div>
            </div>
          </div>

          <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-6">
            Aktuelle Fahrzeuge
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PARTNERS.rent500.vehicles.map((v, i) => (
              <VehicleCard key={v.name + i} vehicle={v} index={i} />
            ))}
          </div>
          <p className="mt-6 font-editorial italic text-lg text-zinc-400">
            Weitere Fahrzeuge folgen demnächst.
          </p>
        </motion.div>

        {/* HIGH END ENTERTAINMENT */}
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "100px" }}
          data-testid="partner-highend"
          className="border-t border-black/5 pt-16 lg:pt-24"
        >
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">
              Foto &amp; Video
            </p>
            <h3 className="font-display uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl mb-6">
              High End Entertainment
            </h3>
            <p className="text-base lg:text-lg leading-relaxed text-zinc-500 mb-8">
              Du brauchst professionelle Fotos, Videos oder Werbecontent — zum
              Beispiel Produktfotos für deine neue Clothing Brand oder deine
              Website? Über mich bekommst du bei High End Entertainment{" "}
              <strong className="text-ink font-medium">garantiert einen Rabatt</strong> —
              wie hoch er ausfällt, sprechen wir vorab persönlich ab. Melde dich
              einfach bei mir und ich handle das für dich aus. Beispielarbeiten
              findest du direkt auf der Website des Partners.
            </p>
            <ul className="space-y-3 mb-10">
              {HEE_SERVICES.map((s) => (
                <li key={s} className="flex items-center gap-4 text-sm uppercase tracking-[0.15em] text-zinc-700">
                  <span className="w-5 h-px bg-gold shrink-0" />
                  {s}
                </li>
              ))}
            </ul>
            <MagneticButton>
              <a
                href={PARTNERS.highEnd.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("highend_click")}
                data-testid="highend-link-button"
                className="group relative overflow-hidden inline-flex bg-ink text-paper px-8 py-4 text-xs uppercase tracking-[0.25em]"
              >
                <span className="absolute inset-0 bg-gold translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
                <span className="relative flex items-center gap-3 group-hover:text-ink transition-colors duration-500">
                  Zu High End Entertainment <ArrowUpRight size={16} />
                </span>
              </a>
            </MagneticButton>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
